import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Wifi, X, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';

export default function Planes() {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [viewingPlan, setViewingPlan] = useState(null);
  const queryClient = useQueryClient();

  const { data: planes, isLoading } = useQuery({
    queryKey: ['planes'],
    queryFn: async () => {
      const response = await api.get('/planes');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/planes', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planes'] });
      toast.success('Plan creado exitosamente');
      setShowModal(false);
    },
    onError: () => toast.error('Error al crear plan'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/planes/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planes'] });
      toast.success('Plan actualizado exitosamente');
      setShowModal(false);
      setEditingPlan(null);
    },
    onError: () => toast.error('Error al actualizar plan'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/planes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['planes'] });
      toast.success('Plan eliminado');
    },
    onError: () => toast.error('Error al eliminar plan'),
  });

  const filteredPlanes = planes?.filter((p) =>
    p.nombre.toLowerCase().includes(search.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      nombre: formData.get('nombre'),
      velocidadBajada: Number(formData.get('velocidadBajada')),
      velocidadSubida: Number(formData.get('velocidadSubida')),
      precioMensual: Number(formData.get('precioMensual')),
      descripcion: formData.get('descripcion'),
    };
    
    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPlan(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Planes de Internet</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus size={20} />
          <span>Nuevo Plan</span>
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar plan..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlanes?.map((plan) => (
          <div key={plan.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-primary-100 rounded-lg">
                <Wifi size={24} className="text-primary-600" />
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setViewingPlan(plan)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <Eye size={16} />
                </button>
                <button 
                  onClick={() => openEditModal(plan)}
                  className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => deleteMutation.mutate(plan.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900">{plan.nombre}</h3>
            <p className="text-gray-500 text-sm mt-1">{plan.descripcion}</p>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Bajada</span>
                <span className="font-medium">{plan.velocidadBajada} Mbps</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subida</span>
                <span className="font-medium">{plan.velocidadSubida} Mbps</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span className="text-gray-900">Precio</span>
                <span className="text-primary-600">${plan.precioMensual}/mes</span>
              </div>
            </div>
            <div className="mt-4">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                plan.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {plan.activo ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Ver Plan */}
      {viewingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Detalle del Plan</h2>
              <button onClick={() => setViewingPlan(null)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Nombre</span>
                <span className="font-medium">{viewingPlan.nombre}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Velocidad de Bajada</span>
                <span className="font-medium">{viewingPlan.velocidadBajada} Mbps</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Velocidad de Subida</span>
                <span className="font-medium">{viewingPlan.velocidadSubida} Mbps</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Precio Mensual</span>
                <span className="font-medium text-primary-600">${viewingPlan.precioMensual}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Descripcion</span>
                <span className="font-medium">{viewingPlan.descripcion || '-'}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Estado</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  viewingPlan.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {viewingPlan.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear/Editar Plan */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingPlan ? 'Editar Plan' : 'Nuevo Plan'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input 
                  name="nombre" 
                  defaultValue={editingPlan?.nombre} 
                  required 
                  className="w-full px-3 py-2 border rounded-lg" 
                  placeholder="Ej: Plan 50Mbps" 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Velocidad Bajada (Mbps)</label>
                  <input 
                    name="velocidadBajada" 
                    type="number" 
                    defaultValue={editingPlan?.velocidadBajada}
                    required 
                    className="w-full px-3 py-2 border rounded-lg" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Velocidad Subida (Mbps)</label>
                  <input 
                    name="velocidadSubida" 
                    type="number" 
                    defaultValue={editingPlan?.velocidadSubida}
                    required 
                    className="w-full px-3 py-2 border rounded-lg" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio Mensual ($)</label>
                <input 
                  name="precioMensual" 
                  type="number" 
                  step="0.01" 
                  defaultValue={editingPlan?.precioMensual}
                  required 
                  className="w-full px-3 py-2 border rounded-lg" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion</label>
                <textarea 
                  name="descripcion" 
                  defaultValue={editingPlan?.descripcion}
                  rows={3} 
                  className="w-full px-3 py-2 border rounded-lg"
                ></textarea>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending 
                    ? 'Guardando...' 
                    : editingPlan ? 'Actualizar' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
